import React, { useContext, useMemo } from 'react';
import { Col, Row, Tooltip } from 'antd';
import { IObject } from '@utils/defaultTypes';
import { getDateToBRPattern } from '@utils/formatters';
import CustomTag from '@components/CustomTag';
import { SharedPlanPayload } from '@pages/Administrative/SharedPlans/NewSharedPlanModal';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { useTranslation } from 'react-i18next';
import { GroupingPayload } from '@pages/Administrative/Groupings/NewGroupingModal';
import { FilterQueryItems } from './types';
import { QueryItem } from './QueryItem';
import { FiltersCardContainer } from './styles';

type QueryBuilderType = {
  clearSearchParams: (paramsToDelete?: string[]) => void;
  setSearchParams: (values: IObject) => void;
  itemsToMap?: FilterQueryItems[];
  handleChange: (value: string, add: boolean, valueToAdd?: string) => void;
  defaultValues?: FilterQueryItems[];
  onChangeSearchUploader: (value: {
    [key: string]: string[] | undefined;
  }) => void;
  departmentId?: string;
};

export const QueryBuilder = ({
  clearSearchParams,
  setSearchParams,
  itemsToMap,
  handleChange,
  defaultValues,
  onChangeSearchUploader,
  departmentId,
}: QueryBuilderType) => {
  const { t } = useTranslation('inventory');
  const { isMotBusiness } = useContext(SessionContext);
  const { lg, xl, xs } = {
    lg: 10,
    xl: 8,
    xs: 24,
  };

  const { data: businessContracts } = useSwr<{
    content: SharedPlanPayload[];
  }>(
    isMotBusiness && departmentId
      ? `/service-proxy/broker/sharing-plans`
      : null,
    {
      params: {
        departmentId,
        linesPerPage: 100,
      },
    },
  );

  const { data: businessGroupings } = useSwr<{
    content: GroupingPayload[];
  }>(
    isMotBusiness && departmentId
      ? '/service-proxy/broker/inventory-groups'
      : null,
    {
      params: {
        linesPerPage: 100,
        departmentId,
      },
    },
  );

  const businessContractsOptions = useMemo(() => {
    const contracts = businessContracts?.content;
    return Array.isArray(contracts)
      ? contracts?.map(contract => ({
          label: `${contract?.id} - ${contract?.description}`,
          value: `${contract?.id}`,
        }))
      : [];
  }, [businessContracts]);

  const businessGroupingsOptions = useMemo(() => {
    const groupings = businessGroupings?.content;
    return Array.isArray(groupings)
      ? groupings?.map(grouping => ({
          label: `${grouping?.id} - ${grouping?.name}`,
          value: `${grouping?.id}`,
        }))
      : [];
  }, [businessGroupings?.content]);

  const treatedItems = useMemo(
    () =>
      itemsToMap?.map(item => {
        if (isMotBusiness) {
          let placeholder = '';
          if (item.value === 'brokerContractId') {
            if (!departmentId) {
              placeholder = t('selectADepartment');
            } else if (
              businessContracts?.content &&
              businessContracts?.content?.length === 0
            ) {
              placeholder = t('departmentWithoutContracts');
            }
            return {
              ...item,
              disabled: !departmentId || businessContractsOptions?.length === 0,
              placeholder,
              options: businessContractsOptions,
            };
          }
          if (item.value === 'accountId') {
            if (!departmentId) {
              placeholder = t('selectADepartment');
            } else if (
              businessGroupings?.content &&
              businessGroupings?.content?.length === 0
            ) {
              placeholder = t('departmentWithoutGroupings');
            }

            return {
              ...item,
              disabled: !departmentId || businessGroupingsOptions?.length === 0,
              placeholder,
              options: businessGroupingsOptions,
            };
          }
        }

        return item;
      }),
    [
      businessContracts?.content,
      businessContractsOptions,
      businessGroupings?.content,
      businessGroupingsOptions,
      departmentId,
      isMotBusiness,
      itemsToMap,
      t,
    ],
  );

  const treatedDefaultValues = useMemo(
    () =>
      defaultValues?.map(item => {
        if (isMotBusiness) {
          if (item.value === 'brokerContractId') {
            return {
              ...item,
              options: businessContractsOptions,
            };
          }
          if (item.value === 'accountId') {
            return {
              ...item,
              options: businessGroupingsOptions,
            };
          }
        }
        return item;
      }),
    [
      businessContractsOptions,
      businessGroupingsOptions,
      defaultValues,
      isMotBusiness,
    ],
  );

  const sliceTheTitle = (item: any) => {
    const itemValue =
      item.options && item.value !== 'lastAlertDateFrom'
        ? item.options.find(
            (optionsItem: any) => optionsItem.value === item.defaultValue,
          )?.label
        : item.defaultValue;

    if (item?.range) {
      const startValue =
        Boolean(itemValue?.split(' ')[0]) && itemValue?.split(' ')[0];
      const endValue =
        Boolean(itemValue?.split(' ')[1]) && itemValue?.split(' ')[1];
      return (
        <>
          <span>{`${item?.name}: `}</span>
          {startValue}
          {startValue && endValue && ' - '}
          {endValue}
        </>
      );
    }
    if (item?.value === 'lastAlertDateFrom') {
      const startDate = getDateToBRPattern(itemValue);

      return (
        <>
          <span>{`${item?.name}: `}</span>
          {startDate}
        </>
      );
    }

    if (item?.date) {
      const startDate =
        Boolean(itemValue?.split(' ')[0]) &&
        getDateToBRPattern(itemValue?.split(' ')[0]);

      const endDate =
        Boolean(itemValue?.split(' ')[1]) &&
        getDateToBRPattern(itemValue?.split(' ')[1]);
      return (
        <>
          <span>{`${item?.name}: `}</span>
          {startDate}
          {startDate && endDate && ' - '}
          {endDate}
        </>
      );
    }
    return itemValue?.length > 32 ? (
      <Tooltip title={itemValue}>
        <span>{`${item?.name}: `}</span>
        {`${itemValue?.slice(0, 32)}...`}
      </Tooltip>
    ) : (
      <>
        <span>{`${item?.name}: `}</span>
        {itemValue}
      </>
    );
  };

  const hasDefaultValue = useMemo(
    () => treatedDefaultValues?.some(item => item.defaultValue),
    [treatedDefaultValues],
  );

  return (
    <FiltersCardContainer>
      {hasDefaultValue && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          {treatedDefaultValues?.map(
            (item: any) =>
              item?.defaultValue && (
                <CustomTag
                  closable
                  onClose={() => {
                    if (item.range) {
                      clearSearchParams([
                        `${item?.range?.from}`,
                        `${item?.range?.to}`,
                      ]);
                    } else if (item.date) {
                      clearSearchParams([
                        `${item?.date?.start}`,
                        `${item?.date?.end}`,
                      ]);
                    } else {
                      onChangeSearchUploader({ [item.value]: undefined });
                      clearSearchParams([item.value]);
                    }

                    handleChange(item.value, false);
                  }}
                  key={item.value}
                >
                  {sliceTheTitle(item)}
                </CustomTag>
              ),
          )}
        </div>
      )}
      <Row align="bottom" gutter={[16, 16]}>
        {treatedItems?.map(item => (
          <Col key={item?.name} lg={lg} xl={xl} xs={xs}>
            <QueryItem
              setSearchParams={setSearchParams}
              clearSearchParams={clearSearchParams}
              valueOfItem={item}
              onChangeSearchUploader={onChangeSearchUploader}
            />
          </Col>
        ))}
      </Row>
    </FiltersCardContainer>
  );
};
